import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FiClock, FiDownload, FiEdit2, FiGithub, FiPauseCircle, FiPlay, FiPlus, FiRefreshCw, FiStopCircle, FiTrash2, FiUpload, FiX } from "react-icons/fi";
import {
  endpoints,
  urlCancelMunshotExecution,
  urlGithubAccount,
  urlGithubBranches,
  urlGithubExecution,
  urlGithubWorkflowRun,
  urlGithubWorkflowTemplate,
  urlMunshotArtifact,
  urlWorkflowSchedule,
} from "../config/api";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import "./GithubActions.css";

const DEFAULT_WORKFLOW = `name: Munshot Deploy

on:
  workflow_dispatch:

jobs:
  deploy:
    runs-on: [self-hosted, linux, muns]
    steps:
      - uses: actions/checkout@v4
      - name: Install dependencies
        run: npm install
      - name: Build
        run: npm run build
`;

const EMPTY_TEMPLATE = {
  name: "",
  description: "",
  workflowFilename: "munshot-workflow.yml",
  workflowYaml: DEFAULT_WORKFLOW,
};
const RUNS_PAGE_SIZE = 25;
const unwrap = (response) => response?.data?.data ?? response?.data;

export const workflowStatus = (run) => {
  if (run?.status === "completed") return { label: "Success", tone: "success" };
  if (run?.status === "failed") return { label: "Failed", tone: "error" };
  if (run?.status === "timed_out") return { label: "Timed out", tone: "error" };
  if (run?.status === "cancelled") return { label: "Cancelled", tone: "muted" };
  if (run?.status === "in_progress" || run?.status === "running") return { label: "Running", tone: "running" };
  if (run?.status === "queued") return { label: "Queued", tone: "queued" };
  return { label: "Unknown", tone: "muted" };
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const authConfig = () => ({
  headers: {
    ...getAuthHeaders(),
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const GithubActions = ({ onClose }) => {
  const [tab, setTab] = useState("templates");
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [runsPage, setRunsPage] = useState(1);
  const [runsTotal, setRunsTotal] = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runners, setRunners] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [accountName, setAccountName] = useState("");
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const tokenInputRef = useRef(null);

  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [runTemplate, setRunTemplate] = useState(null);
  const [runAccountId, setRunAccountId] = useState("");
  const [repositories, setRepositories] = useState([]);
  const [repositorySearch, setRepositorySearch] = useState("");
  const [repositoryFullName, setRepositoryFullName] = useState("");
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [loadingRepositories, setLoadingRepositories] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const [scheduleTemplate, setScheduleTemplate] = useState(null);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [scheduleAccountId, setScheduleAccountId] = useState("");
  const [scheduleRepositories, setScheduleRepositories] = useState([]);
  const [scheduleRepositoryFullName, setScheduleRepositoryFullName] = useState("");
  const [scheduleBranches, setScheduleBranches] = useState([]);
  const [scheduleBranch, setScheduleBranch] = useState("");
  const [scheduleCron, setScheduleCron] = useState("");
  const [scheduleTimezone, setScheduleTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  );
  const [loadingScheduleRepositories, setLoadingScheduleRepositories] = useState(false);
  const [loadingScheduleBranches, setLoadingScheduleBranches] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [selectedExecution, setSelectedExecution] = useState(null);
  const [loadingExecution, setLoadingExecution] = useState(false);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const fetchAccounts = useCallback(async () => {
    const response = await axios.get(endpoints.githubAccounts, authConfig());
    setAccounts(Array.isArray(unwrap(response)) ? unwrap(response) : []);
  }, []);

  const fetchTemplates = useCallback(async () => {
    const response = await axios.get(endpoints.githubWorkflowTemplates, authConfig());
    setTemplates(Array.isArray(unwrap(response)) ? unwrap(response) : []);
  }, []);

  const fetchRuns = useCallback(async (page = 1) => {
    setLoadingRuns(true);
    try {
      const response = await axios.get(endpoints.githubWorkflowRuns, {
        ...authConfig(),
        params: { page, limit: RUNS_PAGE_SIZE },
      });
      const payload = unwrap(response);
      setRuns(Array.isArray(payload?.items) ? payload.items : []);
      setRunsTotal(Number(payload?.total) || 0);
      setRunsPage(Number(payload?.page) || page);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const fetchRunners = useCallback(async () => {
    const response = await axios.get(endpoints.munshotRunners, authConfig());
    setRunners(Array.isArray(unwrap(response)) ? unwrap(response) : []);
  }, []);

  const fetchSchedules = useCallback(async () => {
    const response = await axios.get(endpoints.workflowSchedules, authConfig());
    setSchedules(Array.isArray(unwrap(response)) ? unwrap(response) : []);
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([fetchAccounts(), fetchTemplates(), fetchRuns(), fetchRunners(), fetchSchedules()])
      .catch((err) => mounted && setError(getErrorMessage(err, "Failed to load Munshot Actions.")))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [fetchAccounts, fetchTemplates, fetchRuns, fetchRunners, fetchSchedules]);

  const saveAccount = async (event) => {
    event.preventDefault();
    const token = tokenInputRef.current?.value?.trim();
    if (!accountName.trim() || !token) {
      setError("Account name and PAT are required.");
      return;
    }
    setSavingAccount(true);
    clearMessages();
    try {
      const payload = { name: accountName.trim(), token };
      if (editingAccountId) {
        await axios.put(urlGithubAccount(editingAccountId), payload, authConfig());
      } else {
        await axios.post(endpoints.githubAccounts, payload, authConfig());
      }
      if (tokenInputRef.current) tokenInputRef.current.value = "";
      setAccountName("");
      setEditingAccountId(null);
      setSuccess(editingAccountId ? "GitHub credentials updated." : "GitHub account connected.");
      await fetchAccounts();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save the GitHub account."));
    } finally {
      if (tokenInputRef.current) tokenInputRef.current.value = "";
      setSavingAccount(false);
    }
  };

  const editAccount = (account) => {
    clearMessages();
    setEditingAccountId(account.id);
    setAccountName(account.name);
    tokenInputRef.current?.focus();
  };

  const deleteAccount = async (account) => {
    if (!window.confirm(`Disconnect @${account.github_username}? Existing run history will be preserved.`)) return;
    clearMessages();
    try {
      await axios.delete(urlGithubAccount(account.id), authConfig());
      setSuccess("GitHub account disconnected.");
      await fetchAccounts();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to disconnect the GitHub account."));
    }
  };

  const startNewTemplate = () => {
    clearMessages();
    setTemplateForm({ ...EMPTY_TEMPLATE });
    setEditingTemplateId(null);
    setShowTemplateEditor(true);
  };

  const editTemplate = (template) => {
    clearMessages();
    setTemplateForm({
      name: template.name ?? "",
      description: template.description ?? "",
      workflowFilename: template.workflow_filename ?? "munshot-workflow.yml",
      workflowYaml: template.workflow_yaml ?? DEFAULT_WORKFLOW,
    });
    setEditingTemplateId(template.id);
    setShowTemplateEditor(true);
  };

  const saveTemplate = async (event) => {
    event.preventDefault();
    setSavingTemplate(true);
    clearMessages();
    try {
      if (editingTemplateId) {
        await axios.put(urlGithubWorkflowTemplate(editingTemplateId), templateForm, authConfig());
      } else {
        await axios.post(endpoints.githubWorkflowTemplates, templateForm, authConfig());
      }
      setSuccess(editingTemplateId ? "Workflow template updated." : "Workflow template created.");
      setShowTemplateEditor(false);
      setEditingTemplateId(null);
      await fetchTemplates();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save the workflow template."));
    } finally {
      setSavingTemplate(false);
    }
  };

  const uploadWorkflowYaml = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/\.ya?ml$/i.test(file.name)) {
      setError("Upload a .yml or .yaml workflow file.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setError("Workflow YAML files must be 1 MB or smaller.");
      return;
    }
    clearMessages();
    try {
      const workflowYaml = await file.text();
      setTemplateForm((form) => ({
        ...form,
        workflowFilename: file.name,
        workflowYaml,
      }));
    } catch (err) {
      setError("Could not read the workflow YAML file.");
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete “${template.name}”? This cannot be undone.`)) return;
    clearMessages();
    try {
      await axios.delete(urlGithubWorkflowTemplate(template.id), authConfig());
      setSuccess("Workflow template deleted.");
      await fetchTemplates();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to delete the workflow template."));
    }
  };

  const loadRepositories = useCallback(async (accountId) => {
    if (!accountId) {
      setRepositories([]);
      return;
    }
    setLoadingRepositories(true);
    setError("");
    try {
      const response = await axios.get(endpoints.githubRepositories, {
        ...authConfig(),
        params: { accountId },
      });
      setRepositories(Array.isArray(unwrap(response)) ? unwrap(response) : []);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load repositories."));
      setRepositories([]);
    } finally {
      setLoadingRepositories(false);
    }
  }, []);

  const openRun = (template) => {
    clearMessages();
    const accountId = accounts[0]?.id ?? "";
    setRunTemplate(template);
    setRunAccountId(accountId);
    setRepositoryFullName("");
    setRepositorySearch("");
    setBranches([]);
    setBranch("");
    loadRepositories(accountId);
  };

  const changeRunAccount = (accountId) => {
    setRunAccountId(accountId);
    setRepositoryFullName("");
    setBranches([]);
    setBranch("");
    loadRepositories(accountId);
  };

  const changeRepository = async (fullName) => {
    setRepositoryFullName(fullName);
    setBranches([]);
    setBranch("");
    if (!fullName) return;
    const repository = repositories.find((item) => item.fullName === fullName);
    if (!repository) return;
    setLoadingBranches(true);
    setError("");
    try {
      const response = await axios.get(
        urlGithubBranches(repository.owner, repository.name, runAccountId),
        authConfig(),
      );
      const nextBranches = Array.isArray(unwrap(response)) ? unwrap(response) : [];
      setBranches(nextBranches);
      setBranch(
        nextBranches.some((item) => item.name === repository.defaultBranch)
          ? repository.defaultBranch
          : nextBranches[0]?.name ?? "",
      );
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load repository branches."));
    } finally {
      setLoadingBranches(false);
    }
  };

  const dispatchWorkflow = async (event) => {
    event.preventDefault();
    const repository = repositories.find((item) => item.fullName === repositoryFullName);
    if (!runTemplate || !runAccountId || !repository || !branch) {
      setError("Select a GitHub account, repository, and branch.");
      return;
    }
    setDispatching(true);
    clearMessages();
    try {
      const response = await axios.post(
        urlGithubWorkflowRun(runTemplate.id),
        {
          githubAccountId: runAccountId,
          repositoryOwner: repository.owner,
          repositoryName: repository.name,
          branch,
        },
        authConfig(),
      );
      const execution = unwrap(response);
      setRunTemplate(null);
      const scheduleSync = execution?.scheduleSync;
      if (scheduleSync?.created > 0) {
        setSuccess(`Workflow queued and ${scheduleSync.created} YAML schedule${scheduleSync.created === 1 ? "" : "s"} created.`);
      } else if (scheduleSync?.detected > 0) {
        setSuccess(`Workflow queued. ${scheduleSync.detected} YAML schedule${scheduleSync.detected === 1 ? " is" : "s are"} already registered.`);
      } else {
        setSuccess(
          execution?.runnerAvailable
            ? "Workflow queued on Munshot Actions."
            : "Workflow queued. No Munshot runner is currently online, so it will wait.",
        );
      }
      if (scheduleSync?.errors?.length) {
        setError(`The workflow was queued, but schedule synchronization failed: ${scheduleSync.errors.join("; ")}`);
      }
      await fetchRuns(1);
      setTab("runs");
      if (execution?.id) await loadExecution(execution.id);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to dispatch the workflow."));
    } finally {
      setDispatching(false);
    }
  };

  const loadScheduleRepositories = async (accountId, selectedRepository = "", selectedBranch = "") => {
    setScheduleRepositories([]);
    setScheduleBranches([]);
    setScheduleRepositoryFullName(selectedRepository);
    setScheduleBranch(selectedBranch);
    if (!accountId) return;
    setLoadingScheduleRepositories(true);
    try {
      const response = await axios.get(endpoints.githubRepositories, {
        ...authConfig(),
        params: { accountId },
      });
      const nextRepositories = Array.isArray(unwrap(response)) ? unwrap(response) : [];
      setScheduleRepositories(nextRepositories);
      if (selectedRepository) {
        const repository = nextRepositories.find((item) => item.fullName === selectedRepository);
        if (repository) {
          setLoadingScheduleBranches(true);
          const branchResponse = await axios.get(
            urlGithubBranches(repository.owner, repository.name, accountId),
            authConfig(),
          );
          const nextBranches = Array.isArray(unwrap(branchResponse)) ? unwrap(branchResponse) : [];
          setScheduleBranches(nextBranches);
          setScheduleBranch(
            nextBranches.some((item) => item.name === selectedBranch)
              ? selectedBranch
              : nextBranches.some((item) => item.name === repository.defaultBranch)
                ? repository.defaultBranch
                : nextBranches[0]?.name ?? "",
          );
        }
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load the schedule target."));
    } finally {
      setLoadingScheduleRepositories(false);
      setLoadingScheduleBranches(false);
    }
  };

  const openSchedule = (template) => {
    clearMessages();
    const accountId = accounts[0]?.id ?? "";
    setScheduleTemplate(template);
    setEditingScheduleId(null);
    setScheduleAccountId(accountId);
    setScheduleCron(template.scheduleCrons?.[0] ?? "");
    setScheduleTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    loadScheduleRepositories(accountId);
  };

  const editSchedule = (schedule) => {
    clearMessages();
    const template = templates.find((item) => item.id === schedule.workflow_template_id)
      ?? schedule.workflow_template;
    setScheduleTemplate(template);
    setEditingScheduleId(schedule.id);
    setScheduleAccountId(schedule.github_account_id);
    setScheduleCron(schedule.cron_expression);
    setScheduleTimezone(schedule.timezone || "UTC");
    loadScheduleRepositories(
      schedule.github_account_id,
      `${schedule.repository_owner}/${schedule.repository_name}`,
      schedule.branch,
    );
  };

  const changeScheduleAccount = (accountId) => {
    setScheduleAccountId(accountId);
    loadScheduleRepositories(accountId);
  };

  const changeScheduleRepository = async (fullName) => {
    setScheduleRepositoryFullName(fullName);
    setScheduleBranches([]);
    setScheduleBranch("");
    const repository = scheduleRepositories.find((item) => item.fullName === fullName);
    if (!repository || !scheduleAccountId) return;
    setLoadingScheduleBranches(true);
    try {
      const response = await axios.get(
        urlGithubBranches(repository.owner, repository.name, scheduleAccountId),
        authConfig(),
      );
      const nextBranches = Array.isArray(unwrap(response)) ? unwrap(response) : [];
      setScheduleBranches(nextBranches);
      setScheduleBranch(
        nextBranches.some((item) => item.name === repository.defaultBranch)
          ? repository.defaultBranch
          : nextBranches[0]?.name ?? "",
      );
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load repository branches."));
    } finally {
      setLoadingScheduleBranches(false);
    }
  };

  const saveSchedule = async (event) => {
    event.preventDefault();
    const repository = scheduleRepositories.find(
      (item) => item.fullName === scheduleRepositoryFullName,
    );
    if (!scheduleTemplate || !scheduleAccountId || !repository || !scheduleBranch) {
      setError("Select a workflow, GitHub account, repository, and branch.");
      return;
    }
    setSavingSchedule(true);
    clearMessages();
    try {
      const payload = {
        workflowTemplateId: scheduleTemplate.id,
        githubAccountId: scheduleAccountId,
        repositoryOwner: repository.owner,
        repositoryName: repository.name,
        branch: scheduleBranch,
        cronExpression: scheduleCron.trim(),
        timezone: scheduleTimezone.trim() || "UTC",
      };
      if (editingScheduleId) {
        await axios.put(urlWorkflowSchedule(editingScheduleId), payload, authConfig());
      } else {
        await axios.post(endpoints.workflowSchedules, payload, authConfig());
      }
      setScheduleTemplate(null);
      setEditingScheduleId(null);
      setSuccess(editingScheduleId ? "Workflow schedule updated." : "Workflow schedule created.");
      await fetchSchedules();
      setTab("schedules");
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save the workflow schedule."));
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleSchedule = async (schedule) => {
    clearMessages();
    try {
      await axios.put(
        urlWorkflowSchedule(schedule.id),
        { isActive: !schedule.is_active },
        authConfig(),
      );
      setSuccess(schedule.is_active ? "Workflow schedule paused." : "Workflow schedule resumed.");
      await fetchSchedules();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update the workflow schedule."));
    }
  };

  const deleteSchedule = async (schedule) => {
    if (!window.confirm(`Delete the schedule for “${schedule.workflow_template?.name ?? "Workflow"}”?`)) return;
    clearMessages();
    try {
      await axios.delete(urlWorkflowSchedule(schedule.id), authConfig());
      setSuccess("Workflow schedule deleted.");
      await fetchSchedules();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to delete the workflow schedule."));
    }
  };

  const loadExecution = useCallback(async (id, quiet = false) => {
    if (!quiet) setLoadingExecution(true);
    try {
      const response = await axios.get(urlGithubExecution(id), authConfig());
      setSelectedExecution(unwrap(response));
    } catch (err) {
      if (!quiet) setError(getErrorMessage(err, "Failed to refresh the workflow run."));
    } finally {
      if (!quiet) setLoadingExecution(false);
    }
  }, []);

  const cancelExecution = async (id) => {
    clearMessages();
    try {
      await axios.post(urlCancelMunshotExecution(id), {}, authConfig());
      setSuccess("Cancellation requested.");
      await loadExecution(id);
      await fetchRuns(runsPage);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to cancel the workflow run."));
    }
  };

  const downloadArtifact = async (executionId, artifactId) => {
    clearMessages();
    try {
      const response = await axios.get(urlMunshotArtifact(executionId, artifactId), authConfig());
      const payload = unwrap(response);
      if (payload?.url) window.open(payload.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getErrorMessage(err, "Failed to open the artifact."));
    }
  };

  useEffect(() => {
    if (tab !== "runs" || !selectedExecution?.id) return undefined;
    if (["completed", "failed", "cancelled", "timed_out"].includes(selectedExecution.status)) return undefined;
    const timer = window.setInterval(async () => {
      await loadExecution(selectedExecution.id, true);
      await fetchRuns(runsPage);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [tab, selectedExecution?.id, selectedExecution?.status, runsPage, loadExecution, fetchRuns]);

  useEffect(() => {
    if (!selectedExecution) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedExecution(null);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedExecution]);

  const filteredRepositories = useMemo(() => {
    const needle = repositorySearch.trim().toLowerCase();
    return repositories.filter((item) => !needle || item.fullName.toLowerCase().includes(needle));
  }, [repositories, repositorySearch]);

  const runsTotalPages = Math.max(1, Math.ceil(runsTotal / RUNS_PAGE_SIZE));
  const runPageNumbers = useMemo(() => {
    const first = Math.max(1, Math.min(runsPage - 2, runsTotalPages - 4));
    const last = Math.min(runsTotalPages, first + 4);
    return Array.from({ length: last - first + 1 }, (_, index) => first + index);
  }, [runsPage, runsTotalPages]);

  const changeRunsPage = async (page) => {
    const nextPage = Math.max(1, Math.min(page, runsTotalPages));
    if (nextPage === runsPage || loadingRuns) return;
    setSelectedExecution(null);
    clearMessages();
    try {
      await fetchRuns(nextPage);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load workflow history."));
    }
  };

  if (loading) return <div className="gha-loading">Loading Munshot Actions…</div>;

  return (
    <div className="gha-page">
      <header className="gha-header">
        <div>
          <span className="gha-eyebrow"><FiGithub /> Self-hosted automation</span>
          <h2>Munshot Actions</h2>
          <p>Run compatible workflow YAML for private repositories on infrastructure controlled by Munshot.</p>
        </div>
        <button type="button" className="gha-button gha-button--ghost" onClick={onClose}>Close</button>
      </header>

      {error && <div className="gha-alert gha-alert--error" role="alert">{error}</div>}
      {success && <div className="gha-alert gha-alert--success" role="status">{success}</div>}

      <nav className="gha-tabs" aria-label="Munshot Actions sections">
        {[["templates", "Workflows"], ["schedules", "Schedules"], ["accounts", "Repository access"], ["runners", "Runners"], ["runs", "Run history"]].map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? "active" : ""} onClick={() => { clearMessages(); setTab(id); }}>
            {label}
          </button>
        ))}
      </nav>

      {tab === "accounts" && (
        <section className="gha-section">
          <div className="gha-section-heading">
            <div><h3>Private repository access</h3><p>The PAT is used to clone and push selected private repositories. It is encrypted and never returned.</p></div>
          </div>
          <form className="gha-account-form" onSubmit={saveAccount}>
            <label><span>Display name</span><input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Muns GitHub" maxLength={255} /></label>
            <label><span>{editingAccountId ? "Replacement PAT" : "Personal access token"}</span><input ref={tokenInputRef} type="password" autoComplete="new-password" placeholder="github_pat_…" /></label>
            <div className="gha-form-actions">
              <button className="gha-button gha-button--primary" disabled={savingAccount}>{savingAccount ? "Validating…" : editingAccountId ? "Update connection" : "Connect account"}</button>
              {editingAccountId && <button type="button" className="gha-button gha-button--ghost" onClick={() => { setEditingAccountId(null); setAccountName(""); if (tokenInputRef.current) tokenInputRef.current.value = ""; }}>Cancel</button>}
            </div>
          </form>
          <div className="gha-card-list">
            {accounts.length === 0 ? <div className="gha-empty">No GitHub account configured.</div> : accounts.map((account) => (
              <article className="gha-card gha-account-card" key={account.id}>
                <div><strong>{account.name}</strong><span>@{account.github_username}</span></div>
                <div className="gha-row-actions"><button type="button" title="Replace PAT" onClick={() => editAccount(account)}><FiEdit2 /></button><button type="button" title="Disconnect" onClick={() => deleteAccount(account)}><FiTrash2 /></button></div>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === "runners" && (
        <section className="gha-section">
          <div className="gha-section-heading">
            <div><h3>Munshot runners</h3><p>Workers heartbeat every fifteen seconds and are marked offline after one minute.</p></div>
            <button type="button" className="gha-button gha-button--ghost" onClick={fetchRunners}><FiRefreshCw /> Refresh</button>
          </div>
          <div className="gha-card-list">
            {runners.length === 0 ? <div className="gha-empty">No runner has registered yet.</div> : runners.map((runner) => (
              <article className="gha-card gha-account-card" key={runner.id}>
                <div><strong>{runner.name}</strong><span>{(runner.labels || []).join(", ") || "No labels"} · Last seen {formatDate(runner.last_seen_at)}</span></div>
                <span className={`gha-status gha-status--${runner.status === "offline" ? "muted" : runner.status === "busy" ? "running" : "success"}`}>{runner.status}</span>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === "templates" && (
        <section className="gha-section">
          <div className="gha-section-heading"><div><h3>Workflow templates</h3><p>Munshot executes saved YAML directly; it does not commit workflow files into repositories.</p></div><button type="button" className="gha-button gha-button--primary" onClick={startNewTemplate}><FiPlus /> New workflow</button></div>
          {templates.length === 0 ? <div className="gha-empty">Create the first reusable workflow template.</div> : <div className="gha-template-grid">{templates.map((template) => (
            <article className="gha-card gha-template-card" key={template.id}>
              <div className="gha-template-card__top"><div><h4>{template.name}</h4><code>{template.workflow_filename}</code></div><span>{formatDate(template.updated_at ?? template.created_at)}</span></div>
              <p>{template.description || "No description"}</p>
              {template.scheduleCrons?.length > 0 && <div className="gha-cron-list">{template.scheduleCrons.map((cron, index) => <code key={`${cron}-${index}`}><FiClock /> {cron}</code>)}</div>}
              <div className="gha-card-actions"><button type="button" className="gha-button gha-button--primary" onClick={() => openRun(template)} disabled={!accounts.length}><FiPlay /> Run</button><button type="button" className="gha-button gha-button--ghost" onClick={() => openSchedule(template)} disabled={!accounts.length}><FiClock /> Schedule</button><button type="button" className="gha-button gha-button--ghost" onClick={() => editTemplate(template)}><FiEdit2 /> Edit</button><button type="button" className="gha-icon-danger" title="Delete template" onClick={() => deleteTemplate(template)}><FiTrash2 /></button></div>
            </article>
          ))}</div>}
          {!accounts.length && templates.length > 0 && <p className="gha-hint">Connect a GitHub account before running a workflow.</p>}
        </section>
      )}

      {tab === "schedules" && (
        <section className="gha-section">
          <div className="gha-section-heading"><div><h3>Workflow schedules</h3><p>Cron entries run through pg-boss in the selected timezone.</p></div><button type="button" className="gha-button gha-button--ghost" onClick={fetchSchedules}><FiRefreshCw /> Refresh</button></div>
          <div className="gha-table-wrap"><table className="gha-table"><thead><tr><th>Workflow</th><th>Target</th><th>Cron</th><th>Next run</th><th>Status</th><th>Actions</th></tr></thead><tbody>
            {schedules.length === 0 ? <tr><td colSpan="6" className="gha-empty-cell">No workflow schedules configured.</td></tr> : schedules.map((schedule) => <tr key={schedule.id}><td><strong>{schedule.workflow_template?.name ?? "Workflow"}</strong><br /><small>{schedule.github_account?.name ?? "GitHub account"}</small></td><td>{schedule.repository_owner}/{schedule.repository_name}<br /><code>{schedule.branch}</code></td><td><code>{schedule.cron_expression}</code><br /><small>{schedule.timezone}</small></td><td>{schedule.is_active ? formatDate(schedule.next_run_at) : "Paused"}{schedule.last_error && <span className="gha-schedule-error">{schedule.last_error}</span>}</td><td><span className={`gha-status gha-status--${schedule.is_active ? "success" : "muted"}`}>{schedule.is_active ? "Active" : "Paused"}</span></td><td><div className="gha-row-actions"><button type="button" title="Edit schedule" onClick={() => editSchedule(schedule)}><FiEdit2 /></button><button type="button" title={schedule.is_active ? "Pause schedule" : "Resume schedule"} onClick={() => toggleSchedule(schedule)}>{schedule.is_active ? <FiPauseCircle /> : <FiPlay />}</button><button type="button" title="Delete schedule" onClick={() => deleteSchedule(schedule)}><FiTrash2 /></button></div></td></tr>)}
          </tbody></table></div>
        </section>
      )}

      {tab === "runs" && (
        <section className="gha-section">
          <div className="gha-section-heading"><div><h3>Workflow runs</h3><p>All runs are retained in history. Open one to view its live details and logs.</p></div><button type="button" className="gha-button gha-button--ghost" onClick={() => fetchRuns(runsPage)} disabled={loadingRuns}><FiRefreshCw /> {loadingRuns ? "Refreshing…" : "Refresh"}</button></div>
          <div className="gha-table-wrap" aria-busy={loadingRuns}><table className="gha-table"><thead><tr><th>Workflow</th><th>Repository</th><th>Branch</th><th>Status</th><th>Queued</th></tr></thead><tbody>
            {runs.length === 0 ? <tr><td colSpan="5" className="gha-empty-cell">No workflows have been queued.</td></tr> : runs.map((run) => { const status = workflowStatus(run); return <tr key={run.id} tabIndex="0" onClick={() => loadExecution(run.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") loadExecution(run.id); }} className={selectedExecution?.id === run.id ? "selected" : ""}><td>{run.workflow_template?.name ?? "Workflow"}</td><td>{run.repository_owner}/{run.repository_name}</td><td><code>{run.branch}</code></td><td><span className={`gha-status gha-status--${status.tone}`}>{status.label}</span></td><td>{formatDate(run.queued_at)}</td></tr>; })}
          </tbody></table></div>
          {runsTotal > 0 && <nav className="gha-pagination" aria-label="Workflow run history pages">
            <span>Showing {(runsPage - 1) * RUNS_PAGE_SIZE + 1}–{Math.min(runsPage * RUNS_PAGE_SIZE, runsTotal)} of {runsTotal}</span>
            <div>
              <button type="button" onClick={() => changeRunsPage(1)} disabled={runsPage === 1 || loadingRuns} aria-label="First page">«</button>
              <button type="button" onClick={() => changeRunsPage(runsPage - 1)} disabled={runsPage === 1 || loadingRuns} aria-label="Previous page">‹</button>
              {runPageNumbers.map((page) => <button type="button" key={page} className={page === runsPage ? "active" : ""} onClick={() => changeRunsPage(page)} disabled={loadingRuns} aria-current={page === runsPage ? "page" : undefined}>{page}</button>)}
              <button type="button" onClick={() => changeRunsPage(runsPage + 1)} disabled={runsPage === runsTotalPages || loadingRuns} aria-label="Next page">›</button>
              <button type="button" onClick={() => changeRunsPage(runsTotalPages)} disabled={runsPage === runsTotalPages || loadingRuns} aria-label="Last page">»</button>
            </div>
          </nav>}
        </section>
      )}

      {selectedExecution && (
        <div className="gha-run-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedExecution(null); }}>
          <aside className="gha-run-drawer" role="dialog" aria-modal="true" aria-label="Workflow run details">
            {loadingExecution && <div className="gha-run-drawer__loading">Refreshing run…</div>}
            <ExecutionDetails execution={selectedExecution} onClose={() => setSelectedExecution(null)} onCancel={cancelExecution} onArtifact={downloadArtifact} />
          </aside>
        </div>
      )}

      {showTemplateEditor && (
        <div className="gha-modal-backdrop" role="presentation"><div className="gha-modal gha-modal--wide" role="dialog" aria-modal="true" aria-labelledby="workflow-editor-title">
          <div className="gha-modal-header"><div><h3 id="workflow-editor-title">{editingTemplateId ? "Edit workflow" : "New workflow"}</h3><p>YAML must define <code>on.workflow_dispatch</code> or <code>on.schedule</code>.</p></div><button type="button" onClick={() => setShowTemplateEditor(false)} aria-label="Close"><FiX /></button></div>
          <form onSubmit={saveTemplate} className="gha-editor-form">
            <div className="gha-form-grid"><label><span>Name</span><input required value={templateForm.name} onChange={(e) => setTemplateForm((form) => ({ ...form, name: e.target.value }))} /></label><label><span>Workflow filename</span><input required value={templateForm.workflowFilename} onChange={(e) => setTemplateForm((form) => ({ ...form, workflowFilename: e.target.value }))} /></label></div>
            <label><span>Description</span><input value={templateForm.description} onChange={(e) => setTemplateForm((form) => ({ ...form, description: e.target.value }))} /></label>
            <div className="gha-yaml-field"><div className="gha-yaml-heading"><span>Workflow YAML</span><label className="gha-upload-button"><FiUpload /> Upload .yml<input type="file" accept=".yml,.yaml,application/x-yaml,text/yaml" onChange={uploadWorkflowYaml} /></label></div><textarea className="gha-yaml" required spellCheck="false" value={templateForm.workflowYaml} onChange={(e) => setTemplateForm((form) => ({ ...form, workflowYaml: e.target.value }))} /></div>
            <div className="gha-form-actions gha-form-actions--end"><button type="button" className="gha-button gha-button--ghost" onClick={() => setShowTemplateEditor(false)}>Cancel</button><button className="gha-button gha-button--primary" disabled={savingTemplate}>{savingTemplate ? "Validating…" : "Save workflow"}</button></div>
          </form>
        </div></div>
      )}

      {scheduleTemplate && (
        <div className="gha-modal-backdrop" role="presentation"><div className="gha-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-workflow-title">
          <div className="gha-modal-header"><div><h3 id="schedule-workflow-title">{editingScheduleId ? "Edit schedule" : "Schedule workflow"}</h3><p>{scheduleTemplate.name}</p></div><button type="button" onClick={() => { setScheduleTemplate(null); setEditingScheduleId(null); }} aria-label="Close"><FiX /></button></div>
          <form onSubmit={saveSchedule} className="gha-editor-form">
            <label><span>GitHub account</span><select required value={scheduleAccountId} onChange={(e) => changeScheduleAccount(e.target.value)}><option value="">Select account</option>{accounts.map((account) => <option key={account.id} value={account.id}>{account.name} (@{account.github_username})</option>)}</select></label>
            <label><span>Repository</span><select required value={scheduleRepositoryFullName} disabled={loadingScheduleRepositories} onChange={(e) => changeScheduleRepository(e.target.value)}><option value="">{loadingScheduleRepositories ? "Loading repositories…" : "Select repository"}</option>{scheduleRepositories.map((repo) => <option key={repo.id} value={repo.fullName}>{repo.fullName}{repo.private ? " (private)" : ""}</option>)}</select></label>
            <label><span>Branch</span><select required value={scheduleBranch} disabled={loadingScheduleBranches || !scheduleRepositoryFullName} onChange={(e) => setScheduleBranch(e.target.value)}><option value="">{loadingScheduleBranches ? "Loading branches…" : "Select branch"}</option>{scheduleBranches.map((item) => <option key={item.name} value={item.name}>{item.name}{item.protected ? " (protected)" : ""}</option>)}</select></label>
            <div className="gha-form-grid"><label><span>Cron expression</span><input required list="workflow-cron-options" value={scheduleCron} onChange={(e) => setScheduleCron(e.target.value)} placeholder="0 8 * * 1" /><datalist id="workflow-cron-options">{scheduleTemplate.scheduleCrons?.map((cron) => <option key={cron} value={cron} />)}</datalist></label><label><span>Timezone</span><input required value={scheduleTimezone} onChange={(e) => setScheduleTimezone(e.target.value)} placeholder="UTC" /></label></div>
            {scheduleTemplate.scheduleCrons?.length > 0 && <p className="gha-hint">Cron entries from the workflow YAML are available in the cron field.</p>}
            <div className="gha-form-actions gha-form-actions--end"><button type="button" className="gha-button gha-button--ghost" onClick={() => { setScheduleTemplate(null); setEditingScheduleId(null); }}>Cancel</button><button className="gha-button gha-button--primary" disabled={savingSchedule}>{savingSchedule ? "Saving…" : editingScheduleId ? "Save changes" : "Create schedule"}</button></div>
          </form>
        </div></div>
      )}

      {runTemplate && (
        <div className="gha-modal-backdrop" role="presentation"><div className="gha-modal" role="dialog" aria-modal="true" aria-labelledby="run-workflow-title">
          <div className="gha-modal-header"><div><h3 id="run-workflow-title">Run workflow</h3><p>{runTemplate.name} · executed directly by Munshot Actions</p></div><button type="button" onClick={() => setRunTemplate(null)} aria-label="Close"><FiX /></button></div>
          <form onSubmit={dispatchWorkflow} className="gha-editor-form">
            <label><span>GitHub account</span><select required value={runAccountId} onChange={(e) => changeRunAccount(e.target.value)}><option value="">Select account</option>{accounts.map((account) => <option key={account.id} value={account.id}>{account.name} (@{account.github_username})</option>)}</select></label>
            <label><span>Filter repositories</span><input value={repositorySearch} onChange={(e) => setRepositorySearch(e.target.value)} placeholder="Search by owner or repository" /></label>
            <label><span>Repository</span><select required value={repositoryFullName} disabled={loadingRepositories} onChange={(e) => changeRepository(e.target.value)}><option value="">{loadingRepositories ? "Loading repositories…" : "Select repository"}</option>{filteredRepositories.map((repo) => <option key={repo.id} value={repo.fullName}>{repo.fullName}{repo.private ? " (private)" : ""}</option>)}</select></label>
            <label><span>Branch</span><select required value={branch} disabled={loadingBranches || !repositoryFullName} onChange={(e) => setBranch(e.target.value)}><option value="">{loadingBranches ? "Loading branches…" : "Select branch"}</option>{branches.map((item) => <option key={item.name} value={item.name}>{item.name}{item.protected ? " (protected)" : ""}</option>)}</select></label>
            <div className="gha-run-summary"><span>Execution</span><code>Private checkout → Munshot runner → logs and artifacts</code></div>
            <div className="gha-form-actions gha-form-actions--end"><button type="button" className="gha-button gha-button--ghost" onClick={() => setRunTemplate(null)}>Cancel</button><button className="gha-button gha-button--primary" disabled={dispatching}>{dispatching ? "Queueing…" : "Queue workflow"}</button></div>
          </form>
        </div></div>
      )}
    </div>
  );
};

const ExecutionDetails = ({ execution, onClose, onCancel, onArtifact }) => {
  const status = workflowStatus(execution);
  const active = ["queued", "running", "in_progress"].includes(execution.status);
  return <div className="gha-execution">
    <div className="gha-execution__header"><div><span className={`gha-status gha-status--${status.tone}`}>{status.label}</span><h4>{execution.workflow_template?.name ?? "Workflow run"}</h4></div><button type="button" onClick={onClose} aria-label="Close run details"><FiX /></button></div>
    <dl><div><dt>Repository</dt><dd>{execution.repository_owner}/{execution.repository_name}</dd></div><div><dt>Branch</dt><dd><code>{execution.branch}</code></dd></div><div><dt>Commit</dt><dd><code>{execution.commit_sha?.slice(0, 12) || "Waiting for checkout"}</code></dd></div><div><dt>Runner</dt><dd>{execution.runner?.name ?? "Waiting for runner"}</dd></div><div><dt>Started</dt><dd>{formatDate(execution.started_at ?? execution.queued_at)}</dd></div></dl>
    {active && <button type="button" className="gha-button gha-button--danger" onClick={() => onCancel(execution.id)} disabled={execution.cancel_requested}><FiStopCircle /> {execution.cancel_requested ? "Cancellation requested" : "Cancel run"}</button>}
    {execution.error_message && <div className="gha-alert gha-alert--error">{execution.error_message}</div>}
    <div className="gha-jobs"><h5>Jobs and steps</h5>{execution.jobs?.length ? execution.jobs.map((job) => <div className="gha-job-block" key={job.id}><div className="gha-job"><div><strong>{job.name}</strong><span>{Array.isArray(job.runs_on) ? job.runs_on.join(", ") : job.runs_on}</span></div><span className={`gha-status gha-status--${workflowStatus({ status: job.status === "completed" ? (job.conclusion === "success" ? "completed" : job.conclusion === "skipped" ? "cancelled" : "failed") : job.status }).tone}`}>{job.conclusion || job.status}</span></div>{job.steps?.map((step) => <div className="gha-step" key={step.id}><span>{step.step_index + 1}. {step.name}</span><span>{step.conclusion || step.status}</span></div>)}</div>) : <p>No job has been claimed yet.</p>}</div>
    <div className="gha-log"><div className="gha-log__heading"><h5>Live log</h5><span>Latest 250 KB</span></div><pre>{execution.log_excerpt || "Waiting for runner output…"}</pre></div>
    <div className="gha-artifacts"><h5>Artifacts</h5>{execution.artifacts?.length ? <div className="gha-artifact-list">{execution.artifacts.map((artifact) => <button type="button" className="gha-button gha-button--ghost" key={artifact.id} onClick={() => onArtifact(execution.id, artifact.id)}><FiDownload /> {artifact.name} ({formatBytes(artifact.size_bytes)})</button>)}</div> : <p>Artifacts appear here when upload steps complete. Every run also publishes its full log and generated-file manifest.</p>}</div>
  </div>;
};

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index ? 1 : 0)} ${units[index]}`;
};

export default GithubActions;
